// hooks/useCallNudge.ts
// ──────────────────────────────────────────────────────────────
// Tactical nudge engine for P2P calls.
// Detects negotiation states (not keywords), fires rarely,
// outputs 4–12 word cues. Replaces useRealtimeRebuttal.
// Target: 2–4 interventions per call. Trust through restraint.
// ──────────────────────────────────────────────────────────────

import { useState, useCallback, useRef } from "react";

// ═══════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════

export type NudgeType =
  | "guideline_misapplication"
  | "authority_barrier"
  | "non_clinical_denial"
  | "missing_criteria"
  | "moving_goalposts"
  | "wrong_frame"
  | "time_pressure_close";

export interface Nudge {
  type: NudgeType;
  cue: string;           // 4–12 word tactical prompt
  priority: number;      // 1 = highest
  timestamp: number;
}

export interface NudgeState {
  activeNudge: Nudge | null;
  nudgeHistory: Nudge[];
  totalFired: number;
}

export interface UseCallNudgeOptions {
  enabled?: boolean;
  maxNudgesPerCall?: number;
  cooldownMs?: number;
  autoDismissMs?: number;
}

export interface CaseContext {
  request_type?: string;
  request_details?: string;
  diagnoses?: string;
  denial_reason?: string;
  insurance_company?: string;
  tried_already?: string;
}

// ═══════════════════════════════════════════════════════════════
// PRIORITY MAP (lower = higher priority)
// ═══════════════════════════════════════════════════════════════

const PRIORITY: Record<NudgeType, number> = {
  guideline_misapplication: 1,   // Safety-adjacent — highest
  authority_barrier: 2,          // Structural
  non_clinical_denial: 2,        // Structural
  missing_criteria: 3,           // Conversation control
  moving_goalposts: 3,           // Conversation control
  wrong_frame: 3,                // Conversation control
  time_pressure_close: 4,        // Documentation improvement
};

// ═══════════════════════════════════════════════════════════════
// NEGOTIATION STATE DETECTORS
// ═══════════════════════════════════════════════════════════════
// Each detector returns a cue string if the state is detected,
// or null if not. Patterns test for negotiation dynamics,
// not individual keywords.

interface Detector {
  type: NudgeType;
  test: (text: string, ctx: DetectionContext) => string | null;
}

interface DetectionContext {
  caseContext: CaseContext;
  conversationHistory: string[];
  previousNudgeTypes: Set<NudgeType>;
  callDurationSec: number;
}

const DETECTORS: Detector[] = [
  // ── 1. Guideline Misapplication ──
  // Reviewer cites a guideline but applies it to the wrong population,
  // wrong severity, or wrong clinical context.
  {
    type: "guideline_misapplication",
    test: (text, ctx) => {
      const citesGuideline = /(?:guidelines?|criteria|protocol|evidence|studies|literature)\s+(?:say|state|indicate|suggest|show|recommend)/i.test(text);
      const citesForDifferent = /(?:for\s+(?:most|typical|general|standard|average|uncomplicated))|(?:in\s+(?:most|typical|general|standard)\s+(?:cases|patients|situations))/i.test(text);
      const misappliesPopulation = /(?:(?:at (?:that|this) age)|(?:(?:male|female)s?\s+(?:typically|usually|generally))|(?:patients\s+(?:like|with)\s+(?:this|that)))/i.test(text);

      if (citesGuideline && (citesForDifferent || misappliesPopulation)) {
        return "Ask: does that guideline apply to patients with this condition?";
      }

      // Cites criteria but patient has documented exceptions
      const citesRequirement = /(?:requires?|must|need to|has to)\s+(?:have|show|demonstrate|fail|try|complete)/i.test(text);
      const butAlready = ctx.caseContext.tried_already && ctx.caseContext.tried_already.length > 20;
      const mentionsConservative = /(?:conservative|first.?line|initial|standard)\s+(?:therapy|treatment|management|approach)/i.test(text);

      if (citesRequirement && mentionsConservative && butAlready) {
        return "Ask: which specific treatments are still required to approve?";
      }

      return null;
    },
  },

  // ── 2. Authority Barrier ──
  // Reviewer signals they can't approve, need to escalate, or
  // defer to committee/medical director.
  {
    type: "authority_barrier",
    test: (text) => {
      // Allow filler words (actually, really, just, etc.) between key phrases
      const lacksAuthority = /(?:can'?t|cannot|can\s+not|am\s+(?:not\s+)?(?:able|authorized|permitted))\s+(?:\w+\s+){0,3}(?:approve|authorize|make\s+(?:that|this|a)\s+(?:decision|call|determination))/i.test(text);
      const defersUp = /(?:(?:need|have)\s+to\s+(?:\w+\s+){0,2}(?:send|refer|forward|escalate|submit))|(?:medical\s+director)|(?:committee\s+(?:review|decision))|(?:another\s+(?:reviewer|physician))|(?:refer\s+(?:this|it|the\s+case))/i.test(text);
      const willGetBack = /(?:(?:get|come)\s+back\s+to\s+you)|(?:(?:we'?ll|I'?ll)\s+(?:let\s+you\s+know|follow\s+up|call\s+(?:you\s+)?back))|(?:(?:you'?ll|you\s+will)\s+(?:hear|get\s+a\s+(?:call|letter|determination)))/i.test(text);
      const cantDecide = /(?:(?:don'?t|do\s+not)\s+have\s+(?:the\s+)?authority)|(?:(?:above|beyond|outside)\s+(?:my|the)\s+(?:authority|scope|level))|(?:not\s+(?:my|the)\s+(?:call|decision)\s+to\s+make)/i.test(text);

      if (lacksAuthority || defersUp || cantDecide) {
        return "Ask: who can approve this, and when will they review it?";
      }
      if (willGetBack) {
        return "Ask: who will follow up, by what date, and how?";
      }
      return null;
    },
  },

  // ── 3. Non-Clinical Denial ──
  // Administrative, benefit, or policy reason presented as if it
  // were a clinical determination.
  {
    type: "non_clinical_denial",
    test: (text) => {
      const adminReason = /(?:(?:not\s+(?:a\s+)?covered?\s+benefit)|(?:plan\s+(?:doesn'?t|does\s+not)\s+(?:cover|include))|(?:excluded?\s+(?:from|under|by)\s+(?:the\s+)?(?:plan|benefit|policy))|(?:benefit\s+(?:limitation|exclusion|restriction)))/i.test(text);
      const priorAuthAdmin = /(?:(?:prior\s+auth(?:orization)?\s+(?:wasn'?t|was\s+not)\s+(?:obtained|submitted|received))|(?:(?:expired?|lapsed?|late)\s+(?:authorization|prior\s+auth)))/i.test(text);
      const networkIssue = /(?:(?:out.?of.?network)|(?:not\s+(?:an?\s+)?(?:participating|in.?network|contracted)\s+(?:provider|facility)))/i.test(text);

      if (adminReason || priorAuthAdmin || networkIssue) {
        return "Ask: is this a benefit exclusion or a medical necessity denial?";
      }
      return null;
    },
  },

  // ── 4. Missing Criteria ──
  // Reviewer denies or pushes back without citing a specific
  // rule, criteria set, or clinical standard.
  {
    type: "missing_criteria",
    test: (text, ctx) => {
      const deniesOrResists = /(?:(?:doesn'?t|does\s+not|don'?t)\s+(?:meet|qualify|satisfy|support))|(?:(?:can'?t|cannot|unable\s+to)\s+(?:approve|authorize|support))|(?:(?:not\s+(?:medically\s+)?(?:necessary|indicated|appropriate|warranted|justified)))/i.test(text);
      const noCriteriaCited = !/(?:(?:InterQual|MCG|Milliman|guidelines?|criteria)\s+(?:say|state|require|indicate))|(?:according\s+to\s+(?:our|the)\s+(?:criteria|guidelines|policy))|(?:(?:section|rule|code)\s+\d)/i.test(text);

      if (deniesOrResists && noCriteriaCited) {
        return "Ask: which specific criteria is this patient not meeting?";
      }
      return null;
    },
  },

  // ── 5. Moving Goalposts ──
  // New requirement introduced after a previous objection was addressed.
  // Requires conversation history to detect shift.
  {
    type: "moving_goalposts",
    test: (text, ctx) => {
      if (ctx.conversationHistory.length < 3) return null;

      const newRequirement = /(?:(?:also|additionally|another\s+(?:thing|issue|concern))|(?:(?:but\s+)?(?:what\s+about|there'?s\s+also|we\s+(?:also|still)\s+need))|(?:(?:in\s+addition|furthermore|on\s+top\s+of\s+(?:that|this))))/i.test(text);
      const priorAddressed = /(?:(?:I\s+understand|okay|I\s+see)\s+(?:that|but))|(?:(?:even\s+(?:so|still|with\s+that)))/i.test(text);

      if (newRequirement && priorAddressed) {
        return "Ask: what is the complete list of requirements to approve this?";
      }

      // Reviewer concedes one point then raises another
      const concedes = /(?:(?:I\s+(?:can\s+)?see\s+that)|(?:that\s+(?:makes\s+sense|is\s+(?:fair|reasonable)))|(?:granted|okay|fair\s+enough|I'?ll\s+give\s+you\s+that))/i.test(text);
      const butNewIssue = /(?:(?:but|however|still|though)\s+.{0,30}?(?:concern|issue|question|problem|requirement))/i.test(text);

      if (concedes && butNewIssue) {
        return "Ask: are these all the remaining requirements?";
      }
      return null;
    },
  },

  // ── 6. Wrong Frame ──
  // Reviewer is evaluating the wrong clinical question
  // (e.g., debating diagnosis when the issue is treatment choice).
  {
    type: "wrong_frame",
    test: (text, ctx) => {
      // Reviewer questions the diagnosis when request is about treatment
      const questionsDx = /(?:(?:are\s+(?:you|we)\s+sure\s+(?:about\s+)?(?:the|this)\s+diagnosis)|(?:(?:the\s+)?diagnosis\s+(?:isn'?t|is\s+not)\s+(?:clear|confirmed|established))|(?:could\s+(?:it|this)\s+be\s+(?:something\s+else|another)))/i.test(text);
      const requestIsTreatment = ctx.caseContext.request_type &&
        /(?:medication|procedure|surgery|infusion|therapy|treatment|device)/i.test(ctx.caseContext.request_type);

      if (questionsDx && requestIsTreatment) {
        return "Redirect: this request is about treatment, not diagnosis";
      }

      // Reviewer debates a different level of care than requested
      const debatesSetting = /(?:(?:(?:could|should|can)\s+(?:this|that|it)\s+(?:be\s+done|happen)\s+(?:in|at)\s+(?:a|an|the))|(?:(?:outpatient|inpatient|office|ambulatory|observation)))/i.test(text);
      const requestIsSpecific = ctx.caseContext.request_details &&
        /(?:inpatient|outpatient|office|ambulatory|observation)/i.test(ctx.caseContext.request_details);

      if (debatesSetting && !requestIsSpecific) {
        return "Redirect: clarify what clinical decision is actually being requested";
      }

      return null;
    },
  },

  // ── 7. Time Pressure Close ──
  // Call nearing end without resolution. Triggered by duration
  // + signals the reviewer is wrapping up.
  {
    type: "time_pressure_close",
    test: (text, ctx) => {
      const wrappingUp = /(?:(?:anything\s+else)|(?:(?:is|was)\s+there\s+anything)|(?:(?:I\s+think\s+)?(?:we'?re|that'?s)\s+(?:about\s+)?(?:done|finished|all|it|wrapped))|(?:(?:let\s+me|I'?ll)\s+(?:go\s+ahead\s+and\s+)?(?:finalize|complete|submit|process)\s+(?:this|the)))/i.test(text);
      const isLongEnough = ctx.callDurationSec >= 120; // At least 2 min into call

      if (wrappingUp && isLongEnough) {
        return "Before hanging up \u2014 restate the clinical risk of delaying care";
      }

      // Reviewer gives a closing summary that's unfavorable
      const closingSummary = /(?:(?:so\s+)?(?:(?:I'?m|we'?re)\s+going\s+to\s+(?:deny|uphold|maintain))|(?:(?:my|the)\s+(?:determination|decision|recommendation)\s+(?:is|will\s+be)))/i.test(text);
      if (closingSummary) {
        return "Ask: what specifically would change this determination?";
      }

      return null;
    },
  },
];

// ═══════════════════════════════════════════════════════════════
// MANUAL NUDGE CUES
// ═══════════════════════════════════════════════════════════════
// When the physician taps a quick-tap button, surface this cue.
// These are the single most useful cue per nudge type.

export const MANUAL_CUES: Record<NudgeType, string> = {
  authority_barrier: "Ask: who can approve this, and when will they review it?",
  missing_criteria: "Ask: which specific criteria is this patient not meeting?",
  guideline_misapplication: "Ask: does that guideline apply to patients with this condition?",
  moving_goalposts: "Ask: what is the complete list of requirements to approve this?",
  wrong_frame: "Redirect: this request is about treatment, not diagnosis",
  non_clinical_denial: "Ask: is this a benefit exclusion or a medical necessity denial?",
  time_pressure_close: "Before hanging up — restate the clinical risk of delaying care",
};

// ═══════════════════════════════════════════════════════════════
// QUICK-TAP BUTTON DEFINITIONS
// ═══════════════════════════════════════════════════════════════
// Labels describe what the reviewer said, not abstract categories.
// The physician pattern-matches what they heard → taps → gets cue.

export interface QuickTapButton {
  type: NudgeType;
  label: string;       // What the reviewer said (2-4 words)
  detail: string;      // One-line clarification shown on hover/long-press
}

export const QUICK_TAP_BUTTONS: QuickTapButton[] = [
  {
    type: "authority_barrier",
    label: "Can\u2019t approve",
    detail: "Reviewer can\u2019t decide or needs to escalate",
  },
  {
    type: "missing_criteria",
    label: "No criteria cited",
    detail: "Denied without naming a specific rule",
  },
  {
    type: "moving_goalposts",
    label: "Added new requirement",
    detail: "New objection after addressing the last one",
  },
  {
    type: "guideline_misapplication",
    label: "Wrong guideline used",
    detail: "Cited criteria that don\u2019t apply to this patient",
  },
  {
    type: "non_clinical_denial",
    label: "Policy, not medical",
    detail: "Benefit exclusion framed as clinical decision",
  },
  {
    type: "time_pressure_close",
    label: "Wrapping up",
    detail: "Call ending without resolution",
  },
];

export function useCallNudge({
  enabled = true,
  maxNudgesPerCall = 4,
  cooldownMs = 30000,       // 30s between nudges minimum
  autoDismissMs = 8000,     // Auto-dismiss after 8s
}: UseCallNudgeOptions = {}) {

  // ── State ──
  const [state, setState] = useState<NudgeState>({
    activeNudge: null,
    nudgeHistory: [],
    totalFired: 0,
  });

  // ── Refs (mutable, no re-renders) ──
  const caseContextRef = useRef<CaseContext>({});
  const conversationRef = useRef<string[]>([]);
  const lastNudgeTime = useRef(0);
  const firedTypesRef = useRef<Set<NudgeType>>(new Set());
  const recentCuesRef = useRef<Set<string>>(new Set());
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callStartRef = useRef<number>(Date.now());
  const totalFiredRef = useRef(0);
  const textBufferRef = useRef("");
  const prevChunkRef = useRef("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Set case context (call once when entering call mode) ──
  const setCaseContext = useCallback((ctx: CaseContext) => {
    caseContextRef.current = ctx;
  }, []);

  // ── Start call (resets everything) ──
  const startCall = useCallback(() => {
    callStartRef.current = Date.now();
    totalFiredRef.current = 0;
    firedTypesRef.current.clear();
    recentCuesRef.current.clear();
    conversationRef.current = [];
    textBufferRef.current = "";
    prevChunkRef.current = "";
    setState({
      activeNudge: null,
      nudgeHistory: [],
      totalFired: 0,
    });
  }, []);

  // ── Dismiss active nudge ──
  const dismissNudge = useCallback(() => {
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    setState(prev => ({ ...prev, activeNudge: null }));
  }, []);

  // ── Fire a nudge ──
  const fireNudge = useCallback((type: NudgeType, cue: string) => {
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);

    const nudge: Nudge = {
      type,
      cue,
      priority: PRIORITY[type],
      timestamp: Date.now(),
    };

    totalFiredRef.current += 1;
    lastNudgeTime.current = Date.now();
    firedTypesRef.current.add(type);
    recentCuesRef.current.add(cue);

    setState(prev => ({
      activeNudge: nudge,
      nudgeHistory: [...prev.nudgeHistory, nudge],
      totalFired: totalFiredRef.current,
    }));

    // Auto-dismiss
    dismissTimerRef.current = setTimeout(() => {
      setState(prev => ({
        ...prev,
        activeNudge: prev.activeNudge?.timestamp === nudge.timestamp
          ? null
          : prev.activeNudge,
      }));
    }, autoDismissMs);
  }, [autoDismissMs]);

  // ── Process transcript chunk ──
  // Called by CallTranscription on each new transcript segment.
  const processTranscript = useCallback((text: string) => {
    if (!enabled) return;

    // Buffer text and debounce to avoid processing mid-sentence
    textBufferRef.current += " " + text;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      const currentBuffer = textBufferRef.current.trim();
      if (!currentBuffer || currentBuffer.length < 15) {
        textBufferRef.current = "";
        return;
      }

      // Build analysis text: previous chunk + current buffer
      // This catches phrases split across debounce windows
      const fullText = prevChunkRef.current
        ? (prevChunkRef.current + " " + currentBuffer).trim()
        : currentBuffer;

      // Store current as previous for next window
      prevChunkRef.current = currentBuffer;
      textBufferRef.current = "";

      // Store in conversation history (keep last 20 chunks)
      conversationRef.current.push(currentBuffer);
      if (conversationRef.current.length > 20) {
        conversationRef.current.shift();
      }

      // ── Gate checks ──
      // Max nudges reached for this call
      if (totalFiredRef.current >= maxNudgesPerCall) {
        return;
      }

      // Cooldown not elapsed
      if (Date.now() - lastNudgeTime.current < cooldownMs) {
        return;
      }

      // ── Run detectors against sliding window ──
      const ctx: DetectionContext = {
        caseContext: caseContextRef.current,
        conversationHistory: conversationRef.current,
        previousNudgeTypes: firedTypesRef.current,
        callDurationSec: Math.floor((Date.now() - callStartRef.current) / 1000),
      };

      const candidates: Array<{ type: NudgeType; cue: string; priority: number }> = [];

      for (const detector of DETECTORS) {
        const cue = detector.test(fullText, ctx);
        if (cue) {
          candidates.push({
            type: detector.type,
            cue,
            priority: PRIORITY[detector.type],
          });
        }
      }

      // ── Select highest-priority candidate ──
      if (candidates.length > 0) {
        candidates.sort((a, b) => a.priority - b.priority);
        const winner = candidates[0];

        // Suppress if this exact cue was already shown
        if (recentCuesRef.current.has(winner.cue)) {
          return;
        }

        // Suppress if this type was the most recent nudge
        // (prevents hammering the same negotiation state)
        const lastNudge = state.nudgeHistory[state.nudgeHistory.length - 1];
        if (lastNudge && lastNudge.type === winner.type) {
          return;
        }

        fireNudge(winner.type, winner.cue);
      }
    }, 1200); // 1.2s debounce — fast enough for live calls
  }, [enabled, maxNudgesPerCall, cooldownMs, fireNudge, state.nudgeHistory]);

  // ── Manual nudge trigger (physician taps quick-tap button) ──
  // Bypasses all gates: no cooldown, no debounce, no max limit.
  // If the physician taps it, they need it now.
  const triggerManualNudge = useCallback((type: NudgeType) => {
    const cue = MANUAL_CUES[type];
    if (!cue) return;

    // Fire immediately, bypassing all gates
    fireNudge(type, cue);
  }, [fireNudge]);

  // ── Reset (for call end) ──
  const reset = useCallback(() => {
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    textBufferRef.current = "";
    prevChunkRef.current = "";
    setState({
      activeNudge: null,
      nudgeHistory: [],
      totalFired: 0,
    });
  }, []);

  return {
    activeNudge: state.activeNudge,
    nudgeHistory: state.nudgeHistory,
    totalFired: state.totalFired,
    processTranscript,
    triggerManualNudge,
    setCaseContext,
    startCall,
    dismissNudge,
    reset,
  };
}